/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";
import {
  searchEmbeddedContent,
  processEmbeddingResults,
  createChunkStorageAtom,
  type EmbeddingSearchResult,
} from "../../shared/embedding-utils";
import { recipes, getAllRecipeIds } from "./recipes/index";

interface RecipeSearchResult extends EmbeddingSearchResult {
  source: string;
}

export const SearchRecipesToolParams = z.object({
  query: z
    .string()
    .describe(
      "The search query to find similar implementation recipes and code patterns",
    ),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Finding implementation guide...' or 'Loading recipe...'",
    ),
});

export type SearchRecipesToolParamsType = z.infer<
  typeof SearchRecipesToolParams
>;

async function searchSimilarRecipes(
  query: string,
): Promise<RecipeSearchResult[]> {
  return searchEmbeddedContent({
    endpoint: "/api/canvasui/search-recipes",
    query,
    maxResults: 3,
  }) as Promise<RecipeSearchResult[]>;
}

export const getSearchRecipesToolDescription = () => {
  return `Searches for implementation recipes and code patterns that can help achieve the user's goal.

🚨 **SEQUENCE-AWARE SEARCH REQUIREMENTS FOR APP INTEGRATIONS:**

For app integrations, search in this STRICT ORDER:
1. **FIRST (CHECKPOINT 1/3)**: Authentication recipes - "authentication operations", "auth recipes", "app connection", "connection setup"
2. **THEN (CHECKPOINT 2/3)**: Step Testing/data recipes - ONLY after auth confirmed by user
3. **FINALLY (CHECKPOINT 3/3)**: Field mapping recipes - ONLY after testing completed

⛔ **SEQUENCE VIOLATIONS**:
- Cannot search for testing recipes until auth confirmed
- Cannot search for field mapping recipes until testing completed
- If searching out of sequence, return sequence error message

MANDATORY: Use category-specific searches before API calls:
- **Authentication operations → Search with: "authentication operations", "auth recipes", "app connection", "connection setup" (MUST BE FIRST)**
- Agent tasks → Search with: "agent recipes", "ai agent setup", "agent configuration"
- Table tasks → Search with: "table recipes", "database setup", "table configuration"
- Forms tasks → Search with: "forms recipes", "form setup"
- Zap tasks → Search with: "zap recipes", "automation setup", "workflow configuration"
- Field mapping tasks → Search with: "field mapping", "configure parameters", "map fields" (MUST BE LAST)

- query: The search query to find similar implementation recipes and code patterns - use for specific technical implementations and category-specific searches.
- If you want to discover phase, use the search recipes tool to find specific information that could help with the API calls or execution flow of that step. If an item is returned, prioritize the instructions in that recipe.
- Always perform category-specific recipe searches BEFORE making API calls in that category to ensure you have the proper implementation guidance.
- **SEQUENCE RULE**: For app integrations, authentication recipes are MANDATORY FIRST SEARCH - all other recipes depend on successful auth completion.`;
};

export const recipeChunksAtom =
  createChunkStorageAtom<RecipeSearchResult>("recipeChunksAtom");

export const searchRecipesToolAtom = atom<
  JotaiTool<z.infer<typeof SearchRecipesToolParams>>
>(() => {
  return {
    name: "search_recipes_tool",
    description: getSearchRecipesToolDescription(),
    parameters: SearchRecipesToolParams,
    execute: atom(null, async (_get, set, context) => {
      const { args: rawArgs } = context;
      let params: SearchRecipesToolParamsType;
      try {
        params = SearchRecipesToolParams.parse(rawArgs || {});
      } catch (error) {
        console.error(
          "[SEARCH_RECIPES_TOOL] Error parsing arguments for searchRecipesTool:",
          error,
        );
        return {
          $error: {
            message: `Error: Invalid arguments provided for search_recipes_tool. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
          },
        };
      }

      const { query } = params;

      try {
        const results = await searchSimilarRecipes(query);

        // Process all results - no toggle gating
        const { chunkMap, summaryResults } = processEmbeddingResults(results);

        set(recipeChunksAtom, chunkMap);

        return {
          content: [
            {
              type: "text",
              text: JSON.stringify(summaryResults, null, 2),
            },
          ],
        };
      } catch (error) {
        console.error(
          "[SEARCH_RECIPES_TOOL] Error in searchRecipesToolExecute:",
          error,
        );
        return {
          $error: {
            message: `Error searching similar recipes: ${error instanceof Error ? error.message : String(error)}`,
          },
        };
      }
    }),
  };
});

searchRecipesToolAtom.debugLabel = "searchRecipesToolAtom";

export const activeRecipeAtom = atom<string | null>(null);
activeRecipeAtom.debugLabel = "activeRecipeAtom";

// Use Recipe Tool Parameters
export const UseRecipeToolParams = z.object({
  recipeName: z.string().describe("The name of the recipe to activate"),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Activating recipe...' or 'Loading tools...'",
    ),
});

export type UseRecipeToolParamsType = z.infer<typeof UseRecipeToolParams>;

export const useRecipeToolDescription = `Activates a specific implementation recipe after you have searched for and found it relevant.

🚨 **SEQUENCE-AWARE ACTIVATION RULES:**
- **Authentication recipes**: Activate IMMEDIATELY when found (CHECKPOINT 1/3 priority)
- **Testing/data recipes**: Can only activate AFTER auth confirmed by user
- **Field mapping recipes**: Can only activate AFTER successful testing

⛔ **SEQUENCE VALIDATION**:
- If activating non-auth recipe without auth confirmation → Block with sequence error
- If activating field mapping recipe without testing completion → Block with sequence error

This will load the recipe's instructions and specialized tools to help with the specific task.
- recipeName: The exact name of the recipe to activate. Use 'source' (${getAllRecipeIds().join(", ")}) as recipeName, not 'hash' (e.g.'axjw6'). Example: source: "zapFieldMapping" → use_recipe({"recipeName": "zapFieldMapping"})
- **CRITICAL**: Authentication recipes have highest priority and must be activated first for app integrations
Only call this tool after you have used search_recipes_tool and determined a recipe is relevant to the user's request.`;

export const useRecipeToolAtom = atom<
  JotaiTool<z.infer<typeof UseRecipeToolParams>>
>(() => ({
  name: "use_recipe",
  description: useRecipeToolDescription,
  parameters: UseRecipeToolParams,
  execute: atom(null, async (_get, set, context) => {
    const { args: rawArgs } = context;
    let params: UseRecipeToolParamsType;
    try {
      params = UseRecipeToolParams.parse(rawArgs || {});
    } catch (error) {
      console.error(
        "[USE_RECIPE_TOOL] Error parsing arguments for useRecipeTool:",
        error,
      );
      return {
        $error: {
          message: `Error: Invalid arguments provided for use_recipe. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      };
    }
    const { recipeName } = params;

    // Always use V2 recipes - feature flag removed
    const recipesToSearch = recipes;

    const recipe = recipesToSearch.find((recipe) => recipe.id === recipeName);
    if (!recipe) {
      return {
        $error: {
          message: `Error: Recipe '${recipeName}' not found. Available recipes: ${getAllRecipeIds().join(", ")}`,
        },
      };
    }

    // Set the active recipe which will trigger the tools to be loaded
    set(activeRecipeAtom, recipeName);
    return {
      content: [
        {
          type: "text",
          text: `Recipe '${recipeName}' activated successfully. Specialized tools and instructions are now available.`,
        },
      ],
    };
  }),
}));

useRecipeToolAtom.debugLabel = "useRecipeToolAtom";

/* eslint-enable sort-exports/sort-exports */
